import { useEffect, useRef, useState, useCallback } from 'react';
import { deliveriesApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { ROLES } from '@/types';

interface TrackingState {
  isTracking: boolean;
  lastUpdated: Date | null;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  error: string | null;
  permissionStatus: 'granted' | 'prompt' | 'denied' | 'unsupported';
}

export function useDeliveryPersonLocationTracker() {
  const { role, isAuthenticated } = useAuthStore();
  const isDeliveryPerson = isAuthenticated && role === ROLES.DELIVERY_PERSON;

  const [state, setState] = useState<TrackingState>({
    isTracking: false,
    lastUpdated: null,
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
    permissionStatus: 'unsupported',
  });

  const lastSentCoords = useRef<{ lat: number; lng: number; time: number } | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const isUpdatingRef = useRef<boolean>(false);

  // Send coordinates to server (save current location only)
  const syncLocation = useCallback(async (lat: number, lng: number, accuracy?: number) => {
    if (isUpdatingRef.current) return;
    const now = Date.now();

    // Throttle updates: only send if moved > 15m or at least 25 seconds elapsed
    if (lastSentCoords.current) {
      const timeDiff = now - lastSentCoords.current.time;
      const latDiff = Math.abs(lat - lastSentCoords.current.lat);
      const lngDiff = Math.abs(lng - lastSentCoords.current.lng);
      // Rough distance check: 0.00015 deg is approx 16 meters
      const movedSignificantly = latDiff > 0.00015 || lngDiff > 0.00015;

      if (!movedSignificantly && timeDiff < 25_000) {
        return;
      }
    }

    isUpdatingRef.current = true;
    try {
      await deliveriesApi.updateDriverLocation(lat, lng, accuracy);
      lastSentCoords.current = { lat, lng, time: now };
      setState((prev) => ({
        ...prev,
        isTracking: true,
        lastUpdated: new Date(),
        latitude: lat,
        longitude: lng,
        accuracy: accuracy ?? null,
        error: null,
        permissionStatus: 'granted',
      }));
    } catch (err: any) {
      // Don't kill tracking on temporary network failure
      console.warn('Failed to update driver live location:', err);
    } finally {
      isUpdatingRef.current = false;
    }
  }, []);

  const startTracking = useCallback(() => {
    if (!isDeliveryPerson) return;
    if (!('geolocation' in navigator)) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
        permissionStatus: 'unsupported',
      }));
      return;
    }

    // Clear existing watch if any
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      maximumAge: 10_000,
      timeout: 15_000,
    };

    // Immediate initial position
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void syncLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
      },
      (err) => {
        let msg = 'Unable to get location';
        let permStatus: TrackingState['permissionStatus'] = 'prompt';
        if (err.code === err.PERMISSION_DENIED) {
          msg = 'Location permission was denied. Please allow location access in your browser.';
          permStatus = 'denied';
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          msg = 'Location information is unavailable.';
        } else if (err.code === err.TIMEOUT) {
          msg = 'Location request timed out.';
        }
        setState((prev) => ({
          ...prev,
          error: msg,
          permissionStatus: permStatus,
          isTracking: false,
        }));
      },
      options,
    );

    // Continuous watch
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        void syncLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setState((prev) => ({
            ...prev,
            error: 'Location permission denied.',
            permissionStatus: 'denied',
            isTracking: false,
          }));
        }
      },
      options,
    );

    watchIdRef.current = id;
    setState((prev) => ({ ...prev, isTracking: true, error: null }));
  }, [isDeliveryPerson, syncLocation]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setState((prev) => ({ ...prev, isTracking: false }));
  }, []);

  // Check permission API if supported
  useEffect(() => {
    if (!isDeliveryPerson) return;

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((perm) => {
          setState((prev) => ({ ...prev, permissionStatus: perm.state }));
          perm.onchange = () => {
            setState((prev) => ({ ...prev, permissionStatus: perm.state }));
            if (perm.state === 'granted') {
              startTracking();
            } else if (perm.state === 'denied') {
              stopTracking();
            }
          };
        })
        .catch(() => {});
    }

    startTracking();

    // Fallback periodic sync every 30s to ensure fresh last_location_update
    const intervalId = window.setInterval(() => {
      if ('geolocation' in navigator && isDeliveryPerson) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            void syncLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
          },
          () => {},
          { enableHighAccuracy: true, timeout: 10_000, maximumAge: 10_000 },
        );
      }
    }, 30_000);

    return () => {
      window.clearInterval(intervalId);
      stopTracking();
    };
  }, [isDeliveryPerson, startTracking, stopTracking, syncLocation]);

  return {
    ...state,
    startTracking,
    stopTracking,
  };
}
